/**
 * input: nodes, marks
 * Desc:
 * 1. connecting yjs with backend
 * 2. schema sync with backend
 * 3. this hook is independent of hooks
 */

import { useEffect, useRef, useState } from 'react';
import config from 'config';
import * as decoding from 'lib0/decoding.js';
import * as encoding from 'lib0/encoding.js';
import { WebsocketProvider } from 'y-websocket';
import { Doc } from 'yjs';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { jsonParse, jsonStringify } from '@lyearn/core/utils/json';
import { SchemaSpec } from '@lyearn/lygo-core';

import currentUser from '../helper/currentUser';
import { dedupeYXmlFragmentByBlockId } from './dedupeYXmlFragmentByBlockId';

const WS_SERVER = `${config.ws_origin}/api/collab`;

const LYEARN_WS_PROTOCOL_ID = 100;
const WS_DISCARDED_CODE = 4405; // backend closed with 4405 when closing ws for discarding changes

const CONNECTING_ATTEMPTS_BEFORE_TOKEN_REFRESH = 5;
const CONNECTING_ATTEMPTS_BEFORE_GIVE_UP = 10;

/**
 * Prefer waiting for Yjs sync before exposing the provider so the editor cannot
 * seed/bind before sync step 2 (that was producing same-id twins). Fall back if
 * the sync event is missed.
 */
const EXPOSE_AFTER_SCHEMA_WITHOUT_SYNC_MS = 2000;

/**
 * Update version whenever any change is done in schema(nodes/marks)
 * todo get form from build time later now it is manual.
 */
const version = 121;

/**
 * Send custom message(ex. schema) to backend over websocket
 * @param ws
 * @param obj
 */
const sendMessage = (ws: WebSocket, obj: Object) => {
  const { data, error } = jsonStringify(obj);
  if (!data) {
    return ErrorReporter.error(error);
  }

  const encoder = encoding.createEncoder();
  encoding.writeVarUint(encoder, LYEARN_WS_PROTOCOL_ID);
  encoding.writeVarString(encoder, data);
  ws.send(encoding.toUint8Array(encoder));
};

export type EDITOR_PARAMS_TYPE = Partial<
  Record<
    | 'rootType'
    | 'rootParentType'
    | 'rootParentId'
    | 'rootWorkspaceId'
    | 'featureType'
    | 'contentId',
    string
  >
>;

const useYWebsocketProvider = (
  { nodes = {}, marks = {} }: SchemaSpec,
  roomId: string | null,
  params?: EDITOR_PARAMS_TYPE,
  onClose?: (discardedBy?: string) => void,
  onDisconnect?: () => void,
) => {
  const mountedRef = useRef(config.env === 'development' ? false : true);
  const [exposedProvider, setExposedProvider] = useState<WebsocketProvider | null>(null);
  const [providerGeneration, setProviderGeneration] = useState(0);
  const connectingAttemptsRef = useRef(0);
  const tokenRefreshCountRef = useRef(0);
  const lastActivityAtRef = useRef(Date.now());
  const roomParamsKeyRef = useRef<string>('');
  const onCloseRef = useRef(onClose);
  const onDisconnectRef = useRef(onDisconnect);
  onCloseRef.current = onClose;
  onDisconnectRef.current = onDisconnect;

  useEffect(() => {
    if (roomId && params && mountedRef.current) {
      const roomParamsKey = `${roomId}:${jsonStringify(params).data ?? ''}`;
      if (roomParamsKeyRef.current !== roomParamsKey) {
        roomParamsKeyRef.current = roomParamsKey;
        tokenRefreshCountRef.current = 0;
      }

      let cancelled = false;
      let schemaReady = false;
      let exposeFallbackTimeout: number | null = null;
      connectingAttemptsRef.current = 0;
      lastActivityAtRef.current = Date.now();
      setExposedProvider(null);

      const yDoc = new Doc();
      let providerParams = { params };
      if (currentUser._aT) {
        // @ts-ignore
        providerParams = { params: { op: 'ws-collab', accessToken: currentUser._aT(), ...params } };
      }

      const provider = new WebsocketProvider(WS_SERVER, roomId, yDoc, providerParams);
      const fragment = yDoc.getXmlFragment('prosemirror');

      const clearExposeFallback = () => {
        if (exposeFallbackTimeout != null) {
          window.clearTimeout(exposeFallbackTimeout);
          exposeFallbackTimeout = null;
        }
      };

      const dedupeRoomDoc = (reason: string) => {
        if (cancelled) {
          return 0;
        }
        let removed = 0;
        yDoc.transact(() => {
          removed = dedupeYXmlFragmentByBlockId(fragment);
        }, 'ws-dedupe');
        if (removed > 0) {
          console.warn('[WS-DEBUG] deduped Y.Doc', { roomId, removed, reason });
        }
        return removed;
      };

      const exposeProvider = (reason: string) => {
        if (cancelled) {
          return;
        }
        // Strip same-id twins from sync merge before React/editor bind (BlockId would
        // otherwise mint brand-new ids for the duplicates).
        dedupeRoomDoc(`before expose (${reason})`);
        clearExposeFallback();
        console.info(`[WS] ${roomId} Exposing provider (${reason})`);
        setExposedProvider(provider);
      };

      // TEMP diagnostics
      let lastChildCount = fragment.length;
      const onYjsStructureChange = () => {
        const childCount = fragment.length;
        const ids: string[] = fragment.toArray().map((child: any) =>
          child?.getAttribute ? String(child.getAttribute('id') ?? '') : '',
        );
        const idCounts = ids.reduce<Record<string, number>>((acc, id) => {
          if (!id) return acc;
          acc[id] = (acc[id] ?? 0) + 1;
          return acc;
        }, {});
        const duplicateIds = Object.entries(idCounts)
          .filter(([, count]) => count > 1)
          .map(([id, count]) => ({ id, count }));

        if (childCount !== lastChildCount || duplicateIds.length > 0) {
          console.warn('[WS-DEBUG] Y.Doc structure', {
            roomId,
            prevChildCount: lastChildCount,
            childCount,
            duplicateIds,
            ids,
          });
          lastChildCount = childCount;
        }

        // If twins appear while already exposed (reconnect sync), remove them ASAP.
        if (duplicateIds.length > 0) {
          queueMicrotask(() => dedupeRoomDoc('post-update microtask'));
        }
      };
      fragment.observeDeep(onYjsStructureChange);

      provider.on('connection-close', function wsOnClose(close: CloseEvent) {
        if (cancelled) {
          return;
        }

        if (close.code === WS_DISCARDED_CODE) {
          const { data } = jsonParse<{ discardedBy?: string }>(close.reason);
          onCloseRef.current?.(data?.discardedBy);

          return;
        }

        // Keep Doc + exposed provider for in-place reconnect (uploads). Do not remount app.
        console.info(`[WS] ${roomId} Closed code: ${close.code}. wasClean: ${close.wasClean}`, {
          reason: close.reason,
          msSinceLastActivity: Date.now() - lastActivityAtRef.current,
          visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
          online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        });
      });

      provider.on('sync', (isSynced: boolean) => {
        lastActivityAtRef.current = Date.now();
        console.info(`[WS] ${roomId} yjs sync: ${isSynced}`);
        if (isSynced && schemaReady) {
          exposeProvider('yjs sync');
        }
      });

      provider.messageHandlers[LYEARN_WS_PROTOCOL_ID] = (encoder, decoder) => {
        lastActivityAtRef.current = Date.now();
        const { code } = JSON.parse(decoding.readVarString(decoder));
        switch (code) {
          case 200: {
            schemaReady = true;
            console.info(`[WS] ${roomId} schema sync 200`);
            // Do NOT expose here alone — wait for Yjs sync so seed/editor cannot run
            // before sync step 2 (logs showed 1→2 / 4→8 twins from readSyncStep2).
            if (provider.synced) {
              exposeProvider('schema 200 + already synced');
            } else {
              clearExposeFallback();
              exposeFallbackTimeout = window.setTimeout(() => {
                exposeProvider('schema 200 fallback (sync event missed)');
              }, EXPOSE_AFTER_SCHEMA_WITHOUT_SYNC_MS);
            }
            break;
          }
          case 404: {
            console.error(`[WS] ${roomId} Server doesn't have schema but UI has`);
            return sendMessage(provider.ws!, { version, nodes, marks, method: 'POST' });
          }
        }
      };

      provider.on('status', ({ status }: { status: string }) => {
        if (cancelled) {
          return;
        }

        lastActivityAtRef.current = Date.now();
        console.info(`[WS] ${roomId} status: ${status}`);
        switch (status) {
          case 'connected': {
            connectingAttemptsRef.current = 0;
            tokenRefreshCountRef.current = 0;
            sendMessage(provider.ws!, { version, method: 'GET' });
            break;
          }
          case 'connecting': {
            connectingAttemptsRef.current += 1;
            const attempts = connectingAttemptsRef.current;

            if (attempts >= CONNECTING_ATTEMPTS_BEFORE_GIVE_UP) {
              console.info(
                `[WS] ${roomId} Giving up soft reconnect after ${attempts} connecting attempts`,
              );
              onDisconnectRef.current?.();
              break;
            }

            if (
              attempts === CONNECTING_ATTEMPTS_BEFORE_TOKEN_REFRESH &&
              tokenRefreshCountRef.current === 0
            ) {
              tokenRefreshCountRef.current = 1;
              cancelled = true;
              console.info(
                `[WS] ${roomId} Recreating provider with fresh token after ${attempts} connecting attempts`,
              );
              setProviderGeneration((generation) => generation + 1);
            }
            break;
          }
        }
      });

      return () => {
        cancelled = true;
        clearExposeFallback();
        fragment.unobserveDeep(onYjsStructureChange);
        console.info(`[WS] ${roomId} Disconnecting (effect cleanup)`, {
          providerGeneration,
        });
        provider?.disconnect();
        setExposedProvider(null);
      };
    }
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerGeneration, roomId, params]);

  return exposedProvider?.roomname === roomId ? exposedProvider : null;
};

export default useYWebsocketProvider;
